import axios from 'axios';

import config from '@/config';
import statesBr from '@/data/states-br.json';
import statesPt from '@/data/states-pt.json';

const zipcodeValidationHandlers = {
  BR: zipcode => /^\d{5}-\d{3}$/.test(zipcode),

  PT: postalCode => /^\d{4}-\d{3}$/.test(postalCode),

  UY: postalCode => /^\d{5}$/.test(postalCode),

  PY: postalCode => /^\d{6}$/.test(postalCode),

  CO: postalCode => /^\d{6}$/.test(postalCode),
};

const zipcodeMaskHandlers = {
  BR: () => [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/],
  PT: () => [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/],
  UY: () => [/\d/, /\d/, /\d/, /\d/, /\d/],
  PY: () => [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  CO: () => [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
};

const zipcodePreparationHandlers = {
  BR: zipcode => zipcode.replace('-', ''),

  PT: postalCode => postalCode.replace('-', ''),

  UY: postalCode => postalCode,

  PY: postalCode => postalCode,

  CO: postalCode => postalCode,
};

const getLocationByZipcodeHandlers = {
  BR: async (instance, zipcode) => {
    try {
      const { data } = await instance.get('/locations', { params: { cep: zipcode } });

      return {
        zipcode: data?.zipcode,
        street: data?.street,
        neighborhood: data?.neighborhood,
        neighborhood_id: data?.neighborhood_id,
        city: data?.city,
        city_id: data?.city_id,
        state: data?.state,
        state_id: data?.state_id,
      };
    } catch (err) {
      if (err.response?.status === 404) {
        console.debug(`[DEBUG] getLocationByZipcode ${AddressService.errors.LOCATION_NOT_FOUND}`, err);

        return { error: AddressService.errors.LOCATION_NOT_FOUND };
      }

      console.debug('[DEBUG] getLocationByZipcode error', err);

      return null;
    }
  },

  PT: async (instance, postalCode) => {
    try {
      const { data } = await instance.get(`/postal-code/${postalCode}`);

      return {
        zipcode: data?.postal_code ?? null,
        street: null,
        neighborhood: data?.parish ?? null,
        neighborhood_id: data?.parish ?? null,
        city: data?.municipality ?? null,
        city_id: data?.municipality ?? null,
        state: data?.district ?? null,
        state_id: data?.district ?? null,
      };
    } catch (err) {
      if (err.response?.status === 404) {
        console.debug(`[DEBUG] getLocationByZipcode ${AddressService.errors.LOCATION_NOT_FOUND}`, err);

        return { error: AddressService.errors.LOCATION_NOT_FOUND };
      }

      console.debug('[DEBUG] getLocationByZipcode error', err);

      return null;
    }
  },

  UY: async () => null,

  PY: async () => null,

  CO: async () => null,
};

const getStateHandlers = {
  BR: () => statesBr,

  PT: () => statesPt,

  UY: () => [],

  PY: () => [],

  CO: () => [],
};

const getCitiesByStateHandlers = {
  BR: async (instance, stateId) => {
    try {
      const { data } = await instance.get('/cities', { params: { state_id: stateId } });

      return data;
    } catch {
      return [];
    }
  },

  PT: async (instance, districtName) => {
    try {
      const { data } = await instance.get(`/district/${districtName}/municipalities`);

      if (!Array.isArray(data) || !data.length) return [];

      return data.map(city => ({
        id: city.name,
        name: city.name,
        tokenized: city.tokenized,
      }));
    } catch {
      return [];
    }
  },

  UY: async () => [],

  PY: async () => [],

  CO: async () => [],
};

const getNeighborhoodsByCityHandlers = {
  BR: async (instance, cityId) => {
    try {
      const { data } = await instance.get('/neighborhoods', { params: { city_id: cityId } });

      return data;
    } catch {
      return [];
    }
  },

  PT: async (instance, cityName) => {
    try {
      const { data } = await instance.get(`/municipality/${cityName}/parishes`);

      if (!Array.isArray(data) || !data.length) return [];

      return data.map(neighborhood => ({
        id: neighborhood.name,
        name: neighborhood.name,
        tokenized: neighborhood.tokenized,
      }));
    } catch {
      return [];
    }
  },

  UY: async () => [],

  PY: async () => [],

  CO: async () => [],
};

class AddressService {
  #country;
  #apiInstance;

  static errors = {
    LOCATION_NOT_FOUND: 'LOCATION_NOT_FOUND',
  };

  constructor(country = 'BR') {
    this.#country = country;

    this.#makeAddressApiInstance();
  }

  #makeAddressApiInstance() {
    this.#apiInstance = axios.create({
      baseURL: this.#country === 'PT' ? config.PT_ADDRESS_API_BASE_URL : config.RV_ADDRESS_API_BASE_URL,
    });
  }

  #prepareZipcode(zipcodeIdentifier) {
    return zipcodePreparationHandlers[this.#country](zipcodeIdentifier);
  }

  validateZipcode(zipcodeIdentifier) {
    if (typeof zipcodeIdentifier !== 'string') return;

    return zipcodeValidationHandlers[this.#country](zipcodeIdentifier);
  }

  async getLocationByZipcode(zipcodeIdentifier) {
    if (!this.validateZipcode(zipcodeIdentifier)) return;

    zipcodeIdentifier = this.#prepareZipcode(zipcodeIdentifier);

    return getLocationByZipcodeHandlers[this.#country](this.#apiInstance, zipcodeIdentifier);
  }

  getStates() {
    return getStateHandlers[this.#country](this.#apiInstance);
  }

  async getCitiesByState(stateIdentifier) {
    return getCitiesByStateHandlers[this.#country](this.#apiInstance, stateIdentifier);
  }

  async getNeighborhoodsByCity(cityIdentifier) {
    return getNeighborhoodsByCityHandlers[this.#country](this.#apiInstance, cityIdentifier);
  }

  getZipcodeMask() {
    return zipcodeMaskHandlers[this.#country]();
  }
}

export default AddressService;
